import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/admin/RoleGuard';
import Layout from './components/common/Layout';
import LoadingSpinner from './components/common/LoadingSpinner';

// Lazy load all pages
const LLNManualReviewPage = React.lazy(() => import('./pages/admin/LLNManualReviewPage'));
const LLNConfigPage = React.lazy(() => import('./pages/admin/LLNConfigPage'));
const LLNUserAttemptsPage = React.lazy(() => import('./pages/admin/LLNUserAttemptsPage'));
const LLNRubricsPage = React.lazy(() => import('./pages/admin/LLNRubricsPage'));
const LLNAIAssessmentDashboardPage = React.lazy(() => import('./pages/admin/LLNAIAssessmentDashboardPage'));
const MCPPromptTemplatesPage = React.lazy(() => import('./pages/admin/MCPPromptTemplatesPage'));
const UserManagementPage = React.lazy(() => import('./pages/admin/UserManagementPage'));
const ACSFCoreSkillsPage = React.lazy(() => import('./pages/admin/ACSFCoreSkillsPage'));
const DigitalReadinessConfigPage = React.lazy(() => import('./pages/admin/DigitalReadinessConfigPage'));
const DigitalReadinessUserAttemptsPage = React.lazy(() => import('./pages/admin/DigitalReadinessUserAttemptsPage'));
const SessionTimeoutConfigPage = React.lazy(() => import('./pages/admin/SessionTimeoutConfigPage'));
const RoleRedirect = React.lazy(() => import('./components/auth/RoleRedirect'));

// Pages
const HomePage = React.lazy(() => import('./pages/Home'));
const LoginPage = React.lazy(() => import('./pages/Login'));
const ProfilePage = React.lazy(() => import('./pages/Profile'));
const NotFoundPage = React.lazy(() => import('./pages/NotFound'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPassword'));
const SetPasswordPage = React.lazy(() => import('./pages/SetPasswordPage'));
const ExpiredInvitePage = React.lazy(() => import('./pages/ExpiredInvitePage'));

// Info Pages
const PrivacyPolicy = React.lazy(() => import('./pages/info/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/info/TermsOfService'));
const ContactUs = React.lazy(() => import('./pages/info/ContactUs'));

// New Dashboard Pages
const UserDashboard = React.lazy(() => import('./pages/user/UserDashboard'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));

// Digital Readiness Pages (user)
const DigitalReadinessLandingPage = React.lazy(() => import('./pages/user/DigitalReadinessLandingPage'));
const DigitalReadinessQuestionnairePage = React.lazy(() => import('./pages/user/DigitalReadinessQuestionnairePage'));
const DigitalReadinessResultsPage = React.lazy(() => import('./pages/user/DigitalReadinessResultsPage'));
const DigitalReadinessHistoryPage = React.lazy(() => import('./pages/user/DigitalReadinessHistoryPage'));

// Digital Readiness Pages (admin)
const AdminCategoriesPage = React.lazy(() => import('./pages/admin/CategoriesPage'));
const AdminQuestionsPage = React.lazy(() => import('./pages/admin/QuestionsPage'));
const AdminRecommendationsPage = React.lazy(() => import('./pages/admin/RecommendationsPage'));
const AdminResponseValuesPage = React.lazy(() => import('./pages/admin/ResponseValuesPage'));
const AdminUserReportsPage = React.lazy(() => import('./pages/admin/UserReportsPage'));
const LLNQuestionsPage = React.lazy(() => import('./pages/admin/LLNQuestionsPage'));
const WrittenResponsesPage = React.lazy(() => import('./pages/admin/WrittenResponsesPage'));
const LLNDiagnosticsPage = React.lazy(() => import('./pages/admin/LLNDiagnosticsPage'));
const LLNUserReportsPage = React.lazy(() => import('./pages/admin/LLNUserReportsPage'));

// LLN Assessment Pages
const LLNQuestionnairePage = React.lazy(() => import('./pages/user/LLNQuestionnairePage'));
const LLNResultsPage = React.lazy(() => import('./pages/user/LLNResultsPage'));
const LLNHistoryPage = React.lazy(() => import('./pages/user/LLNHistoryPage'));
const LLNDetailedReportPage = React.lazy(() => import('./pages/user/LLNDetailedReportPage'));

// LLN Induction Form Pages
const LLNInductionFormHistoryPage = React.lazy(() => import('./pages/user/LLNInductionFormHistoryPage'));
const LLNInductionFormManagementPage = React.lazy(() => import('./pages/admin/LLNInductionFormManagementPage'));
const LLNInductionFormTemplateManagementPage = React.lazy(() => import('./pages/admin/LLNInductionFormTemplateManagementPage'));
const LLNCourseManagementPage = React.lazy(() => import('./pages/admin/LLNCourseManagementPage'));
const SitePagesManagementPage = React.lazy(() => import('./pages/admin/SitePagesManagementPage'));
const PerformanceDashboardPage = React.lazy(() => import('./pages/admin/PerformanceDashboardPage'));

// Components
const LLNSkillsDashboard = React.lazy(() => import('./components/LLNAssessment/LLNSkillsDashboard'));

// Helper function to create a protected route
const createProtectedRoute = (component: React.ReactNode, requiresAdmin: boolean = false) => {
  // For admin routes, wrap with AdminRoute
  if (requiresAdmin) {
    return (
      <ProtectedRoute>
        <AdminRoute>
          {component}
        </AdminRoute>
      </ProtectedRoute>
    );
  }
  
  // For regular user routes
  return (
    <ProtectedRoute>
      {component}
    </ProtectedRoute>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Layout>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />
            <Route path="/expired-invite" element={<ExpiredInvitePage />} />
            
            {/* Info pages - accessible to everyone */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/contact-us" element={<ContactUs />} />

            {/* Role-based redirect after login */}
            <Route
              path="/dashboard"
              element={createProtectedRoute(<RoleRedirect />)}
            />

            {/* New Dashboard routes */}
            <Route
              path="/user/dashboard"
              element={createProtectedRoute(<UserDashboard />)}
            />
            <Route
              path="/profile"
              element={createProtectedRoute(<ProfilePage />)}
            />

            {/* Digital Readiness routes */}
            <Route
              path="/digital-readiness"
              element={createProtectedRoute(<DigitalReadinessLandingPage />)}
            />
            <Route
              path="/digital-readiness/questionnaire"
              element={createProtectedRoute(<DigitalReadinessQuestionnairePage />)}
            />
            <Route
              path="/digital-readiness/results"
              element={createProtectedRoute(<DigitalReadinessResultsPage />)}
            />
            <Route
              path="/digital-readiness/history"
              element={createProtectedRoute(<DigitalReadinessHistoryPage />)}
            />

            {/* LLN Assessment routes (user-facing) */}
            <Route
              path="/lln-assessment"
              element={createProtectedRoute(<LLNSkillsDashboard />)}
            />
            <Route
              path="/lln-assessment/questionnaire"
              element={createProtectedRoute(<LLNQuestionnairePage />)}
            />
            <Route
              path="/lln-assessment/results"
              element={createProtectedRoute(<LLNResultsPage />)}
            />
            <Route
              path="/lln-assessment/history"
              element={createProtectedRoute(<LLNHistoryPage />)}
            />
            <Route
              path="/lln-assessment/detailed-report"
              element={createProtectedRoute(<LLNDetailedReportPage />)}
            />

            {/* LLN Induction Form routes (user-facing) */}
            <Route
              path="/lln-induction-forms"
              element={createProtectedRoute(<LLNInductionFormHistoryPage />)}
            />

            {/* Admin routes */}
            <Route
              path="/admin/dashboard"
              element={createProtectedRoute(<AdminDashboard />, true)}
            />
            <Route
              path="/admin/questions"
              element={createProtectedRoute(<AdminQuestionsPage />, true)}
            />
            <Route
              path="/admin/categories"
              element={createProtectedRoute(<AdminCategoriesPage />, true)}
            />
            <Route
              path="/admin/recommendations"
              element={createProtectedRoute(<AdminRecommendationsPage />, true)}
            />
            <Route
              path="/admin/response-values"
              element={createProtectedRoute(<AdminResponseValuesPage />, true)}
            />
            <Route
              path="/admin/user-reports"
              element={createProtectedRoute(<AdminUserReportsPage />, true)}
            />
            <Route
              path="/admin/lln-questions"
              element={createProtectedRoute(<LLNQuestionsPage />, true)}
            />
            <Route
              path="/admin/written-responses"
              element={createProtectedRoute(<WrittenResponsesPage />, true)}
            />
            <Route
              path="/admin/lln-diagnostics"
              element={createProtectedRoute(<LLNDiagnosticsPage />, true)}
            />
            <Route
              path="/admin/lln-reports"
              element={createProtectedRoute(<LLNUserReportsPage />, true)}
            />
            <Route
              path="/admin/lln-config"
              element={createProtectedRoute(<LLNConfigPage />, true)}
            />
            <Route
              path="/admin/lln-user-attempts"
              element={createProtectedRoute(<LLNUserAttemptsPage />, true)}
            />
            <Route
              path="/admin/lln-manual-review"
              element={createProtectedRoute(<LLNManualReviewPage />, true)}
            />
            <Route
              path="/admin/lln-rubrics"
              element={createProtectedRoute(<LLNRubricsPage />, true)}
            />
            <Route
              path="/admin/lln-ai-dashboard"
              element={createProtectedRoute(<LLNAIAssessmentDashboardPage />, true)}
            />
            <Route
              path="/admin/mcp-prompt-templates"
              element={createProtectedRoute(<MCPPromptTemplatesPage />, true)}
            />
            <Route
              path="/admin/users"
              element={createProtectedRoute(<UserManagementPage />, true)}
            />
            <Route
              path="/admin/acsf-core-skills"
              element={createProtectedRoute(<ACSFCoreSkillsPage />, true)}
            />
            <Route
              path="/admin/lln-induction-forms"
              element={createProtectedRoute(<LLNInductionFormManagementPage />, true)}
            />
            <Route
              path="/admin/lln-induction-form-templates"
              element={createProtectedRoute(<LLNInductionFormTemplateManagementPage />, true)}
            />
            <Route
              path="/admin/lln-courses"
              element={createProtectedRoute(<LLNCourseManagementPage />, true)}
            />
            <Route
              path="/admin/digital-readiness-config"
              element={createProtectedRoute(<DigitalReadinessConfigPage />, true)}
            />
            <Route
              path="/admin/digital-readiness-user-attempts"
              element={createProtectedRoute(<DigitalReadinessUserAttemptsPage />, true)}
            />
            <Route
              path="/admin/session-timeout"
              element={createProtectedRoute(<SessionTimeoutConfigPage />, true)}
            />
            <Route
              path="/admin/site-pages"
              element={createProtectedRoute(<SitePagesManagementPage />, true)}
            />
            <Route
              path="/admin/performance"
              element={createProtectedRoute(<PerformanceDashboardPage />, true)}
            />
            
            {/* Fallback routes */}
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
          </Suspense>
        </Layout>
        <Toaster position="top-right" />
      </Router>
    </AuthProvider>
  );
}

export default App;
